import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MoveToRiskFundPayload,
  RiskFundFilters,
  riskFundService,
} from '../riskFundService';

/**
 * Invalidates every cache a write-off touches: the Risk Fund screen itself, the
 * source document lists (receipts / invoices / admissions), and the financial
 * aggregates that derive receivables and Risk Fund totals from them.
 *
 * Exported because a cancellation writes the balance off internally, so the
 * admission hooks need exactly the same set of invalidations.
 */
export function invalidateRiskFundImpact(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['risk-fund'] });
  queryClient.invalidateQueries({ queryKey: ['risk-fund-summary'] });
  queryClient.invalidateQueries({ queryKey: ['diagnostic-receipts'] });
  queryClient.invalidateQueries({ queryKey: ['diagnostic-receipt'] });
  queryClient.invalidateQueries({ queryKey: ['invoices'] });
  queryClient.invalidateQueries({ queryKey: ['invoice'] });
  queryClient.invalidateQueries({ queryKey: ['admissions'] });
  queryClient.invalidateQueries({ queryKey: ['admission-billing-status'] });
  queryClient.invalidateQueries({ queryKey: ['top-sheet'] });
  queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  queryClient.invalidateQueries({ queryKey: ['cashbook-summary'] });
  queryClient.invalidateQueries({ queryKey: ['patient-invoice-summary'] });
  queryClient.invalidateQueries({ queryKey: ['patient-receipt-summary'] });
}

export function useRiskFundEntries(
  page = 1,
  limit = 20,
  filters: RiskFundFilters = {},
) {
  return useQuery({
    queryKey: ['risk-fund', page, limit, filters],
    queryFn: () => riskFundService.getEntries(page, limit, filters),
    placeholderData: (previous) => previous,
    staleTime: 15000,
  });
}

export function useRiskFundSummary() {
  return useQuery({
    queryKey: ['risk-fund-summary'],
    queryFn: () => riskFundService.getSummary(),
    staleTime: 15000,
  });
}

export function useRiskFundEntry(id?: string) {
  return useQuery({
    queryKey: ['risk-fund', 'entry', id],
    queryFn: () => riskFundService.getEntry(id!),
    enabled: !!id,
    staleTime: 15000,
  });
}

/** Silent so the calling screen can show one contextual summary toast. */
export function useMoveToRiskFund() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (payload: MoveToRiskFundPayload) =>
      riskFundService.moveToRiskFund(payload),
    onSuccess: () => invalidateRiskFundImpact(queryClient),
  });
}

/** Silent: the caller confirms the reversal and shows the result. */
export function useDeleteRiskFundEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (id: string) => riskFundService.deleteEntry(id),
    onSuccess: () => invalidateRiskFundImpact(queryClient),
  });
}

export function useBulkDeleteRiskFundEntries() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (ids: string[]) => riskFundService.bulkDeleteEntries(ids),
    onSuccess: () => invalidateRiskFundImpact(queryClient),
  });
}
